import { useContext, useEffect, useState } from "react";
import {
  AdditionalDocumentForm,
  AdditionalDocumentFormValues,
  AlertComponent,
  DialogComponent,
  ModalComponent,
} from "../../../components";
import { useRequest } from "../../../hooks";
import { ClientProps } from "../../../interfaces/interfaces";
import {
  DocRow,
  DocumentsPortalLayout,
} from "../../../components/documents/DocumentsPortalLayout";
import DataContext from "../../../context/DataContext";

const isSuccessful = (response: any) => response?.success || response?.succes;

const toDateInput = (value?: string) =>
  value ? new Date(value).toISOString().split("T")[0] : "";

export const AdditionalDocumentsAdmin = () => {
  const { handleRequest } = useRequest();
  const { clientOptions, fetchDataClient } = useContext(DataContext);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<DocRow | null>(null);
  const [rowToDelete, setRowToDelete] = useState<DocRow | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => { fetchDataClient(); }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSuccess(false);
      setIsError(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [isSuccess, isError]);

  const fetchDocuments = (client: ClientProps | null) => {
    setSelectedClient(client);
    if (!client) {
      setRows([]);
      return;
    }
    setRowsLoading(true);
    handleRequest({
      endpoint: `additional-document-list/client/${client.id}`,
      options: { method: "GET" },
    })
      .then((response) => {
        const docs: DocRow[] = (isSuccessful(response) ? response.data : []).map(
          (item: any) => ({
            rowId: `ad-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellName: "",
            source: "uploaded" as const,
            path: item.path,
            owner: "—",
            mimeType: item.mimeType,
            originalFileName: item.originalFileName,
          }),
        );
        setRows(docs);
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  };

  const downloadFile = async (row: DocRow, open = false) => {
    if (!row.path) return;
    setIsBusy(true);
    try {
      const response = await handleRequest({
        endpoint: "additional-document-download",
        options: {
          method: "POST",
          body: JSON.stringify({ key: row.path }),
          headers: { responseType: "Buffer" } as any,
        },
      });
      if (!response.success) throw new Error("Download failed");
      const mimeType = response.mimeType || row.mimeType || "application/octet-stream";
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(response.data.data).buffer], { type: mimeType }),
      );
      if (open && mimeType.includes("pdf")) {
        window.open(url);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", row.originalFileName || row.customName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch {
      setIsError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const submitDocument = async (values: AdditionalDocumentFormValues) => {
    setIsBusy(true);
    setIsModalOpen(false);
    try {
      const formData = new FormData();
      formData.append("client", JSON.stringify(values.client));
      formData.append("customName", values.customName);
      values.date && formData.append("installationDate", values.date);
      values.file && formData.append("file", values.file, values.file.name);
      formData.append("type", "additional-document");

      const endpoint = editingRow
        ? `${process.env.REACT_APP_DEV_API}/additional-document/${editingRow.rowId.replace("ad-", "")}`
        : `${process.env.REACT_APP_DEV_API}/additional-document-upload`;
      const response = await fetch(endpoint, {
        method: editingRow ? "PUT" : "POST",
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("info")}` },
      });
      const result = await response.json();
      if (!result.success) throw new Error("Save failed");
      setIsSuccess(true);
      fetchDocuments(values.client);
      setEditingRow(null);
    } catch {
      setIsError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const deleteDocument = () => {
    if (!rowToDelete) return;
    setIsBusy(true);
    handleRequest({
      endpoint: `additional-document/${rowToDelete.rowId.replace("ad-", "")}`,
      options: { method: "DELETE" },
    })
      .then((response) => {
        if (!response.success) throw new Error("Delete failed");
        setIsSuccess(true);
        fetchDocuments(selectedClient);
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setIsBusy(false);
        setRowToDelete(null);
      });
  };

  return (
    <>
      <DocumentsPortalLayout
        title="Additional Documents"
        subtitle="Browse, filter and download client-level documents"
        wells={[]}
        wellsLoading={false}
        rows={rows}
        rowsLoading={rowsLoading || isBusy}
        selectedWellId={null}
        onWellSelect={() => {}}
        onDownload={(row) => downloadFile(row)}
        showSource={false}
        hideWellsPanel
        noWellMessage="Select a client to load additional documents"
        isAdmin
        selectedClientId={selectedClient?.id ?? null}
        clientOptions={clientOptions}
        onClientChange={fetchDocuments}
        onUpload={() => {
          setEditingRow(null);
          setIsModalOpen(true);
        }}
        onOpenInNew={(row) => downloadFile(row, true)}
        onDelete={setRowToDelete}
        onEditUploaded={(row) => {
          setEditingRow(row);
          setIsModalOpen(true);
        }}
        isError={isError}
      />

      {isSuccess && <AlertComponent type="success" />}
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <AdditionalDocumentForm
              clientOptions={clientOptions}
              initialValues={
                editingRow
                  ? {
                      client: selectedClient ?? undefined,
                      customName: editingRow.customName,
                      date: toDateInput(editingRow.date),
                    }
                  : { client: selectedClient ?? undefined }
              }
              requireFile={!editingRow}
              onSubmit={submitDocument}
              onCancel={() => {
                setIsModalOpen(false);
                setEditingRow(null);
              }}
            />
          }
        />
      )}
      {rowToDelete && (
        <DialogComponent
          type="error"
          modalContent={
            <div>Are you sure you want to remove this document?</div>
          }
          onAccept={deleteDocument}
          onCancel={() => setRowToDelete(null)}
        />
      )}
    </>
  );
};
